// Elapsed-time formatting for the "Last seen" field (#119).
//
// The server sends `seconds_since_seen` — an integer, clamped at zero —
// precisely so this module can pick the unit. The older `hours_since_seen`
// cannot be used for this and is why the bug existed: it is hours rounded to
// one decimal, so a sensor that reported a minute ago arrives as `0` and reads
// "0h ago", and `0.3` is unrecoverably somewhere between 15 and 21 minutes.
// The unit was fixed at the API boundary, so no amount of client formatting
// could get minutes back out of it.
//
// Why the plural rules below are hand-written rather than delegated to
// Intl.RelativeTimeFormat: the `me` locale is not in CLDR.
// `new Intl.RelativeTimeFormat('me')` does not throw — it resolves to en-US
// and returns "5 minutes ago", which is worse than the suffix it would be
// replacing and fails invisibly. `sr-Latn` does resolve, but it is ekavian
// ("pre 5 minuta") where Montenegrin is ijekavian ("prije 5 minuta"). Only
// `bs`/`hr` happen to produce the right forms, and pinning Montenegrin
// correctness to a foreign locale tag plus whatever CLDR version each browser
// shipped is not a trade worth making for three units.

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

// Under this, a reading is "just now" rather than "1 minute ago". It also
// absorbs the sub-minute skew that survives the server's clamp, so a device
// running a few seconds fast never produces a jarring label.
const JUST_NOW_BELOW = 45;

/**
 * Montenegrin shares the Bosnian/Croatian/Serbian three-way split:
 *
 *   one    n % 10 === 1 and n % 100 !== 11         1, 21, 31 …
 *   few    n % 10 in 2..4 and n % 100 not in 12..14  2, 3, 4, 22 …
 *   other  everything else                         0, 5..20, 25 …
 */
function slavicCategory(n) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'One';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'Few';
  return 'Other';
}

function englishCategory(n) {
  return n === 1 ? 'One' : 'Other';
}

const PLURAL_CATEGORY = { en: englishCategory, me: slavicCategory };

// A language with no rule of its own gets English's two-way split. Every
// category is defined in every language in translations.js, so an unexpected
// language tag degrades to an English-shaped plural rather than a raw key.
function pluralize(unit, n, t, language) {
  const category = (PLURAL_CATEGORY[language] ?? englishCategory)(n);
  return t(`lastSeen${unit}${category}`, { n });
}

/**
 * Elapsed time in the largest unit that still reads naturally.
 *
 * Note that the value is computed server-side when /api/health is polled, not
 * when this renders — so with a 15-minute poll interval the label can lag the
 * truth by up to one interval. That staleness is not new (it applied equally
 * to `hours_since_seen`), but minute-level precision makes it visible where
 * one-decimal hours hid it. Ticking it forward on the client would trade the
 * server's clock authority back for the browser's, so it is left alone.
 *
 * @param {number|null|undefined} seconds  `seconds_since_seen` from /api/health
 * @param {(key: string, vars?: object) => string} t  translator from useLanguage
 * @param {string} [language]  'en' | 'me'
 * @returns {string|null}  null when there is nothing to say
 */
export function formatLastSeen(seconds, t, language = 'en') {
  const raw = Number(seconds);
  if (seconds == null || !Number.isFinite(raw)) return null;

  // A device whose clock runs ahead stamps a reading in the future. The server
  // clamps this, but the fake-data path and any not-yet-redeployed backend do
  // not, and "-3 minutes ago" is a worse answer than "just now".
  const s = Math.max(0, Math.round(raw));

  if (s < JUST_NOW_BELOW) return t('lastSeenJustNow');

  // Each unit is compared after rounding, not before, so 3580 seconds becomes
  // "1 hour ago" rather than "60 minutes ago".
  const minutes = Math.round(s / MINUTE);
  if (minutes < 60) return pluralize('Minutes', minutes, t, language);

  const hours = Math.round(s / HOUR);
  if (hours < 24) return pluralize('Hours', hours, t, language);

  return pluralize('Days', Math.round(s / DAY), t, language);
}

const pad = (n) => String(n).padStart(2, '0');

/**
 * The full timestamp behind a rounded label, for a `title` tooltip — so
 * "3 days ago" does not throw away the precision the payload already carries.
 *
 * Deliberately a fixed `YYYY-MM-DD HH:MM:SS` in local time rather than
 * toLocaleString: the missing-`me`-locale problem applies there too, and an
 * operator comparing this against a device log wants one unambiguous format,
 * not one that reorders day and month depending on the language toggle.
 *
 * @param {string|null|undefined} iso  `last_seen` from /api/health
 * @returns {string|null}  null when absent or unparseable
 */
export function formatExactTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}
